import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../shared/Toast';

const API_URL = process.env.REACT_APP_API_URL;
const ROSTER_URL = `${API_URL}/api/roster`;

const errMsg = (e) => e?.response?.data?.error || e.message;

// Every placeable group, labelled with its section (partner children included).
export function usePlaceableGroups() {
  const { data } = useQuery({
    queryKey: ['roster'],
    queryFn: () => axios.get(ROSTER_URL).then((r) => r.data),
  });
  return useMemo(() => {
    if (!data) return { sections: [], byId: {} };
    const sections = data.sections
      .filter((s) => s.key !== 'removed')
      .map((s) => ({
        label: s.label,
        groups: s.groups.flatMap((g) => [
          { id: g.id, name: g.kind === 'partner' ? `${g.name} (loose song)` : g.name, kind: g.kind },
          ...g.children.map((c) => ({ id: c.id, name: `${g.name} › ${c.name}`, kind: c.kind })),
        ]),
      }));
    const byId = {};
    sections.forEach((s) => s.groups.forEach((g) => { byId[g.id] = { ...g, section: s.label }; }));
    return { sections, byId };
  }, [data]);
}

export function PlacementEditor({ selected, suggested, onChange, groups }) {
  const label = (id) => (groups.byId[id] ? `${groups.byId[id].section} · ${groups.byId[id].name}` : `#${id}`);
  return (
    <div className="flex items-center gap-2">
      <select
        value={selected ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="flex-1 border rounded px-2 py-1.5 text-sm"
      >
        <option value="">Unassigned (place later)</option>
        {groups.sections.map((s) => (
          <optgroup key={s.label} label={s.label}>
            {s.groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}{g.id === suggested ? '  (suggested)' : ''}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {suggested && selected !== suggested && (
        <button onClick={() => onChange(suggested)} className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
          title={`Suggested: ${label(suggested)}`}>
          Use suggested
        </button>
      )}
    </div>
  );
}

export default function AddRosterSongModal({ onClose }) {
  const toast = useToast();
  const qc = useQueryClient();
  const groups = usePlaceableGroups();
  const [mode, setMode] = useState(null);           // 'id' | 'search'
  const [step, setStep] = useState('input');        // 'input' | 'select' | 'preview'
  const [idInput, setIdInput] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [failed, setFailed] = useState([]);
  const [placements, setPlacements] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/streaming/search-songs`, { query });
      setResults(res.data.results || []);
      setPicked([]);
      setStep('select');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const preview = async () => {
    const ids = mode === 'id'
      ? idInput.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
      : picked;
    if (!ids.length) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${ROSTER_URL}/songs/preview`, { luminate_ids: ids });
      const items = res.data.results || [];
      setPreviews(items);
      setFailed(res.data.failed || []);
      // Default: keep an existing placement, otherwise the artist-based suggestion.
      setPlacements(Object.fromEntries(items.map((it) => [
        it.luminate_id, it.current_group_id ?? it.suggested_group_id ?? null,
      ])));
      setStep('preview');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    const items = previews.map((p) => ({ luminate_id: p.luminate_id, group_id: placements[p.luminate_id] ?? null }));
    onClose();
    const toastId = toast.loading('Adding to roster...', `Processing ${items.length} song(s)`);
    try {
      const res = await axios.post(`${ROSTER_URL}/songs/add`, { items });
      qc.invalidateQueries({ queryKey: ['roster'] });
      if (res.data.failed_count) {
        toast.error('Some songs failed to add', `${res.data.successful_count} added, ${res.data.failed_count} failed`);
      } else {
        toast.success('Added to roster', `${res.data.successful_count} song(s) added`);
      }
    } catch (e) {
      toast.error('Failed to add songs', errMsg(e));
    } finally {
      toast.dismiss(toastId);
    }
  };

  const title = !mode ? 'Add Song to Roster'
    : step === 'preview' ? 'Confirm placement'
    : mode === 'id' ? 'Add Song by ID'
    : step === 'select' ? 'Select Songs' : 'Search for a Song';

  const btn = 'flex-1 px-4 py-2 rounded-md text-sm disabled:bg-gray-300 disabled:cursor-not-allowed';
  const back = (fn) => (
    <button onClick={fn} disabled={loading} className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}>Back</button>
  );

  return (
    <div className="fixed inset-0 bg-gray-600/50 z-50 overflow-y-auto">
      <div className={`relative top-16 mx-auto p-5 border shadow-lg rounded-md bg-white ${step === 'preview' ? 'w-[40rem]' : 'w-96'} max-w-[calc(100%-2rem)]`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
        </div>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {!mode && (
          <div className="space-y-3">
            <button onClick={() => setMode('id')}
              className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Add by Luminate ID
            </button>
            <button onClick={() => setMode('search')}
              className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Search for Song
            </button>
          </div>
        )}

        {mode === 'id' && step === 'input' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Luminate Song ID(s)
              <textarea value={idInput} onChange={(e) => setIdInput(e.target.value)} rows={3}
                placeholder="Paste one or more Luminate IDs (comma or newline separated)"
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </label>
            <div className="flex gap-2">
              {back(() => setMode(null))}
              <button onClick={preview} disabled={loading || !idInput.trim()} className={`${btn} bg-blue-500 text-white hover:bg-blue-600`}>
                {loading ? 'Checking…' : 'Preview placement'}
              </button>
            </div>
          </div>
        )}

        {mode === 'search' && step === 'input' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Song Title
              <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="Enter song title to search..." className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </label>
            <div className="flex gap-2">
              {back(() => setMode(null))}
              <button onClick={search} disabled={loading || !query.trim()} className={`${btn} bg-blue-500 text-white hover:bg-blue-600`}>
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>
        )}

        {mode === 'search' && step === 'select' && (
          <div className="space-y-4">
            <div className="max-h-72 overflow-y-auto">
              {results.length === 0 && <p className="text-sm text-gray-500">No results.</p>}
              {results.map((s) => {
                const on = picked.includes(s.id);
                return (
                  <label key={s.id} className={`flex items-start gap-3 p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-50 ${on ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                    <input type="checkbox" checked={on} className="mt-1"
                      onChange={() => setPicked(on ? picked.filter((x) => x !== s.id) : [...picked, s.id])} />
                    <span className="text-sm font-medium">{s.display_artist_name} - {s.title}</span>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2">
              {back(() => setStep('input'))}
              <button onClick={preview} disabled={loading || picked.length === 0} className={`${btn} bg-blue-500 text-white hover:bg-blue-600`}>
                {loading ? 'Checking…' : `Preview ${picked.length || ''} placement${picked.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">
              Each song goes in one group. The suggestion is the roster artist listed first in the song's artist name; override to any section, partner or Singles.
            </p>
            <div className="max-h-[28rem] overflow-y-auto divide-y">
              {previews.map((p) => (
                <div key={p.luminate_id} className="py-3 space-y-2">
                  <div>
                    <div className="text-sm font-medium">{p.display_artist_name} - {p.title}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1 text-[11px]">
                      {p.current_group_id && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Already on roster</span>}
                      {p.is_removed && <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700">Currently removed</span>}
                      {p.current_dashboard_group && p.current_dashboard_group !== 'Encore' && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">Currently {p.current_dashboard_group}; will move to Encore</span>
                      )}
                      {p.current_group_id && p.suggested_group_id && p.suggested_group_id !== p.current_group_id && (
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          Artist match: {groups.byId[p.suggested_group_id]?.name || `#${p.suggested_group_id}`}
                        </span>
                      )}
                      {!p.current_group_id && !p.suggested_group_id && (
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">No matching roster artist</span>
                      )}
                    </div>
                  </div>
                  <PlacementEditor
                    groups={groups}
                    selected={placements[p.luminate_id] ?? null}
                    suggested={p.current_group_id ?? p.suggested_group_id}
                    onChange={(id) => setPlacements({ ...placements, [p.luminate_id]: id })}
                  />
                </div>
              ))}
            </div>
            {failed.length > 0 && (
              <p className="text-xs text-red-600">Not found: {failed.map((f) => f.luminate_id).join(', ')}</p>
            )}
            <div className="flex gap-2">
              {back(() => setStep(mode === 'search' ? 'select' : 'input'))}
              <button onClick={confirm} disabled={previews.length === 0} className={`${btn} bg-green-500 text-white hover:bg-green-600`}>
                Add {previews.length} song{previews.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
